import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import ChatWidget from '@/components/ChatWidget';
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const WHEEL_IMG = '/assets/wheel.png';

// Subtle floating particles
function Particle({ style }) {
  return (
    <div className="absolute w-[2px] h-[2px] rounded-full"
      style={{
        background: 'rgba(59, 130, 246, 0.3)',
        animation: `grainFloat ${9 + Math.random() * 7}s ease-in infinite`,
        animationDelay: `${Math.random() * 10}s`,
        ...style
      }} />
  );
}
const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  key: i,
  style: { left: `${8 + Math.random() * 84}%`, bottom: `${5 + Math.random() * 40}%` }
}));

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [sessionLimitDialog, setSessionLimitDialog] = useState(false);
  const [sessionLimitInfo, setSessionLimitInfo] = useState(null);
  const [logoutReason, setLogoutReason] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { const t = setTimeout(() => setReady(true), 60); return () => clearTimeout(t); }, []);

  // Check for forced logout reason
  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason) {
      setLogoutReason(reason);
      sessionStorage.removeItem('logout_reason');
    }
  }, []);

  const handleSubmit = async (e, forceLogout = false) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, forceLogout);
      if (result.success) {
        navigate(result.user.role === 'customer' ? '/portal/dashboard' : '/ops/dashboard');
      } else if (result.sessionLimitExceeded) {
        setSessionLimitInfo(result);
        setSessionLimitDialog(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogin = async () => {
    setSessionLimitDialog(false);
    await handleSubmit(null, true);
  };

  return (
    <div className="min-h-screen w-full flex overflow-hidden" style={{ background: 'var(--bg-darker)' }}>

      {/* ── LEFT — Wheel Visual ─────────────────────────────────────── */}
      <div className="hidden lg:flex flex-1 relative items-center justify-center overflow-hidden"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 55%, var(--surface) 0%, var(--bg-darker) 100%)' }}>

        {/* Deep background radial glow behind wheel */}
        <div className="absolute pointer-events-none"
          style={{
            inset: 0,
            background: 'radial-gradient(circle at 50% 52%, rgba(59,130,246,0.08) 0%, rgba(45,55,72,0.15) 40%, transparent 70%)',
          }} />

        {/* Floating particles */}
        {PARTICLES.map(p => <Particle key={p.key} style={p.style} />)}

        {/* Wheel container */}
        <div className="relative flex items-center justify-center" style={{ width: '65%', maxWidth: 520 }}>
          {/* Wheel image - PNG with transparent background */}
          <div className="tire-spin w-full aspect-square relative flex items-center justify-center">
            <img src={WHEEL_IMG} alt="Heavy-duty wheel"
              className="absolute hub-glow"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }} />
          </div>

          {/* Light sweep overlay - centered */}
          <div className="absolute rounded-full light-sweep-anim pointer-events-none"
            style={{
              width: '82%',
              height: '82%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.03) 15deg, transparent 40deg)',
            }} />
        </div>

        {/* Tagline */}
        <p className="absolute bottom-10 left-0 right-0 text-center text-sm tracking-widest uppercase"
          style={{ color: 'var(--text-faint)', fontFamily: 'JetBrains Mono' }}>
          Fleet Maintenance Simplified
        </p>
      </div>

      {/* ── RIGHT — Login Card ──────────────────────────────────────── */}
      <div className={`w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-8 lg:p-12 transition-all duration-700 ${ready ? 'card-reveal' : 'opacity-0 translate-x-6'}`}
        style={{ background: 'var(--bg)' }}>

        <div className="w-full max-w-sm space-y-8">
          {/* Logo & Title */}
          <div className="text-center lg:text-left">
            <Logo className="h-10 mx-auto lg:mx-0 mb-6" />
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>
              Welcome Back
            </h1>
            <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              Sign in to continue to your portal
            </p>
          </div>

          {/* Forced logout message */}
          {logoutReason && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-md border"
              style={{ background: 'var(--warning-muted)', borderColor: 'var(--warning)' }}>
              <AlertTriangle size={18} style={{ color: 'var(--warning)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--warning)' }}>{logoutReason}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-md border"
              style={{ background: 'var(--error-muted)', borderColor: 'var(--error)' }}>
              <AlertTriangle size={18} style={{ color: 'var(--error)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--error)' }}>{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'var(--text-muted)' }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full input-slate"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'var(--text-muted)' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full input-slate pr-12"
                  data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded transition-colors"
                  style={{ color: 'var(--text-muted)' }}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider"
              data-testid="login-submit"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Request Account Section */}
          <div className="pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-center text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              New customer? Get started with your fleet maintenance
            </p>
            <a href="/apply" 
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-['Manrope'] font-bold text-sm transition-colors"
              style={{ background: 'transparent', border: '1px solid var(--primary)', color: 'var(--primary)' }}
              data-testid="request-account-btn">
              Request Account
            </a>
          </div>

          {/* Footer */}
          <p className="text-center text-xs" style={{ color: 'var(--text-faint)' }}>
            © {new Date().getFullYear()} Millennials Maintenance · All rights reserved
          </p>
        </div>
      </div>

      {/* Chat Widget */}
      <ChatWidget />

      {/* Session Limit Exceeded Dialog */}
      <Dialog open={sessionLimitDialog} onOpenChange={setSessionLimitDialog}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--warning)' }}>
              <AlertTriangle size={20} /> Session Limit Reached
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--text-muted)' }}>
              {sessionLimitInfo?.message || 'You have reached the maximum number of active sessions.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-md border" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Active Sessions</span>
                <span className="font-bold font-mono" style={{ color: 'var(--primary)' }}>
                  {sessionLimitInfo?.currentSessions || 0} / {sessionLimitInfo?.maxSessions || 1}
                </span>
              </div>
            </div>
            <p className="text-xs mt-3" style={{ color: 'var(--text-faint)' }}>
              Clicking "Continue" will automatically log out your oldest session and sign you in on this device.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSessionLimitDialog(false)}
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Cancel
            </Button>
            <Button onClick={handleForceLogin} disabled={loading}
              style={{ background: 'var(--primary)', color: 'white' }}>
              {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              Continue & Log Out Other Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
